/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { cloneDeep, isEmpty, orderBy } from 'lodash';
import { withRouter } from 'react-router-dom';
import ShortNameField from '../Common/shortNameField';
import DetailStatus from '../Common/detailStatus';
import DetailHeader from './detailHeader';
import DetailsTab from './detailsTab';
import DetailStoriesTab from './detailStoriesTab';
import Metrics from '../WorkPackage/metrics';
import TargetSelect from '../Common/targetSelect';
import ModalNavigation from '../Common/modalNavigation';
import modalTypes from '../../constants/modalTypes';
import ShareButton from '../Common/shareButton';
import { openModal, updateModal } from '../../actions/modalActions';
import { fetchUsers } from '../../actions/userActions';
import { getAllCadences } from '../../actions/cadenceActions';
import { WorkPackageStatuses } from '../../constants/storyStatusTypes';
import {
  updateWorkPackage,
  willDeleteWorkPackage,
  createWorkPackage,
  getBurndownData,
  getLeanSeries,
  getWorkDensitySeries,
  getCumulativeFlowSeries,
  restoreWorkPackage,
  getChildrenProgress
} from '../../actions/workPackageActions';
import { allows, handleResultAuthorityChange } from '../../utils/authorization';
import { applicationError } from '../../actions/notificationActions';
import {
  isProductAssociatedWithSpace,
  isTeamAssociatedWithSpace
} from '../../utils/spaceHelpers';
import { currentOrganization } from '../../utils/organizationHelpers';
import {
  getModalProductTeam,
  childOrganizationLevel,
  handleOrgLevelChange,
  defaultNewResultAttributes
} from '../../utils/resultModalHelpers';

class FeatureDetail extends React.Component {
  constructor(props) {
    super(props);
    const prefilledValues = { ...defaultNewResultAttributes(props, 'stories') };
    const result =
      props.result.id > 0 ? { ...props.result } : { ...prefilledValues.result };
    this.state = {
      ...prefilledValues,
      isShortNameValid: true,
      activeTab: 'details',
      changingOrgLevel: false,
      result
    };

    this.handleFieldChange = this.handleFieldChange.bind(this);
  }

  componentWillMount() {
    if (this.props.result && this.props.result.id > 0) {
      this.setFeatureValue(this.props.result);
    }

    this.checkIfItemExists();
  }

  componentDidMount() {
    // if its been deleted, cancel all operations
    this.checkIfItemExists();
    const { result } = this.props;
    // pull in data about descendants
    // for R2s, we care about progress of the children of the R2 as opposed to R3/R4 where we want progress of children's children
    if (result?.id) this.props.getChildrenProgress([result]);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.result !== this.props.result &&
      !!(this.props.result && this.props.result.id)
    ) {
      this.setFeatureValue(this.props.result);
    }

    this.checkIfItemExists();
  }

  getMetricData() {
    // determine total user story children
    const progress = this.props.childrenProgress;
    let r1Children;
    if (progress !== undefined && Object.keys(progress).length !== 0) {
      const keys = Object.keys(progress);
      const arr = [];
      keys.forEach((e) => {
        const value = progress[e].children_count;
        arr.push(value);
      });
      const reducer = (prev, curr) => prev + curr;
      r1Children = arr.reduce(reducer);
    }
    return r1Children || 0;
  }

  setFeatureValue = (result) => {
    this.setState({ result, hasChanged: false });
  };

  checkIfItemExists = () => {
    if (
      this.props.id &&
      !(this.props.result && this.props.result.id) &&
      !this.state.changingOrgLevel
    ) {
      this.props.onClose();
      this.props.applicationError(
        'Link you tried to visit is no longer valid.'
      );
    }
  };

  changeOrgLevel = (orgLevel) => {
    const { result } = this.props;
    this.setState({ changingOrgLevel: true }, () => {
      handleOrgLevelChange(orgLevel, result, this.closeForm);
      if (result.level === orgLevel.level) {
        this.setState({ changingOrgLevel: false });
      }
    });
  };

  handleNewFieldChange = (e) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    let hasChanged = true;
    if (e.short_name) {
      resultClone.short_name = e.short_name;
      this.setState({ isShortNameValid: true });
    } else if (e.description) {
      resultClone.description = e.description;
    } else if (e.owner_id || e.owner_id === null) {
      resultClone.owner_id = e.owner_id;
    } else if (e.product_owner_id || e.product_owner_id === null) {
      resultClone.product_owner_id = e.product_owner_id;
    } else if (e.state) {
      resultClone.state = e.state;
    } else if (e.target && e.target.name) {
      resultClone[e.target.name] = e.target.value;
    } else {
      hasChanged = false;
    }
    this.setState({ result: resultClone, hasChanged });
  };

  handleNewParentChange = (option) => {
    const { result } = this.state;
    const newFeature = { ...result, parent_id: option.value };
    this.setState({ result: newFeature, hasChanged: true });
  };

  handleNewCapabilityChange = (selection, selectedVal) => {
    if (selection === selectedVal) return;
    const newFeature = cloneDeep(this.state.result);
    if (selection) {
      newFeature.capabilities.splice(
        newFeature.capabilities.findIndex((item) => item.id === selection)
      );
    }

    newFeature.capabilities.push({ id: selectedVal });
    this.setState({ result: newFeature, hasChanged: true });
  };

  handleFieldChange = (e) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    const field = e;
    const fieldName = Object.keys(field)[0];
    if (fieldName === 'short_name') {
      if (!field.short_name) {
        resultClone.short_name = field.short_name;
        return this.setState({
          isShortNameValid: false,
          result: resultClone
        });
      }
      if (field.short_name === result.short_name) return null;
    }
    this.props.updateWorkPackage(this.state.result.id, field);
    return null;
  };

  handleKeyPress = ({ key, target }) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    if (key === 'Enter' && !result.id) {
      resultClone[target.name] = target.value;
      this.setState({ result: resultClone }, () => this.createNewWorkPackage());
    }
  };

  handleTypeAttributes = (typeAttributes) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    if (result.result_type) {
      resultClone.result_type.type_attributes = typeAttributes;
      this.setState({ result: resultClone });
    }
  };

  addCriteria = (criterion) => {
    const { criteria } = this.state;
    let newCriteria = [...criteria];
    if (criterion.tempId) {
      newCriteria = newCriteria.map((item) => {
        if (item.tempId === criterion.tempId) {
          return {
            ...item,
            short_name: criterion.short_name
          };
        }
        return item;
      });
    } else newCriteria.push({ ...criterion, tempId: criteria.length + 1 });
    this.setState({ criteria: newCriteria });
  };

  removeCriteria = (criterionIndex) => {
    const { criteria } = this.state;
    const newCriteria = criteria.filter((c, index) => index !== criterionIndex);
    this.setState({ criteria: newCriteria });
  };

  addAttachment = (attachment) => {
    const { attachmentList } = this.state;
    const attachmentListClone = cloneDeep(attachmentList);
    this.setState({ attachmentList: attachmentListClone.concat(attachment) });
  };

  removeAttachment = (attachmentIndex) => {
    const { attachmentList } = this.state;
    const newAttachmentList = attachmentList.filter(
      (att, index) => index !== attachmentIndex
    );
    this.setState({ attachmentList: newAttachmentList });
  };

  createTemporaryStory = (tempStory) => {
    const story = tempStory;
    const { stories } = this.state;
    const storyIndex = stories.findIndex(
      (s) => s.temporaryId === story.temporaryId
    );
    if (storyIndex > -1) {
      const newStories = stories.map((s) => {
        if (s.temporaryId === story.temporaryId) {
          return story;
        }
        return s;
      });
      this.setState({ stories: newStories });
    } else {
      story.temporaryId = stories.length + 1;
      story.owner_initials = [];
      story.owners = [];
      const storiesClone = cloneDeep(stories);
      storiesClone.push(story);
      this.setState({ stories: storiesClone });
    }
  };

  removeTemporaryStory = (storyIndex) => {
    const { stories } = this.state;
    const newStories = stories.filter((s, index) => index !== storyIndex);
    this.setState({ stories: newStories });
  };

  updateTemporaryStory = (temporaryId, attrs) => {
    const { stories } = this.state;
    const storiesClone = cloneDeep(stories);
    const newStories = storiesClone.map((s) =>
      s.temporaryId === temporaryId ? { ...s, ...attrs } : s
    );
    this.setState({ stories: newStories });
  };

  createNewWorkPackage = () => {
    sessionStorage.removeItem('result');
    sessionStorage.removeItem('criteria');
    sessionStorage.removeItem('attachmentList');
    sessionStorage.removeItem('stories');
    const { result, criteria, attachmentList, stories } = this.state;
    const { organizationLevels } = this.props;
    if (!result.short_name) {
      this.setState({ isShortNameValid: false });
      return;
    }
    const cadenceId = result.cadence_id;
    const resultType = {
      ...result.result_type,
      organization_level_id: this.props.organizationLevelId
    };
    const { state } = result;

    this.props.createWorkPackage({
      criteria,
      state,
      data: { work_package: { ...result } },
      cadence: cadenceId,
      type: resultType,
      attachments: attachmentList,
      children: JSON.parse(JSON.stringify(stories)),
      modalId: 'newFeature',
      childOrgLevel: {
        organization_level_id: childOrganizationLevel(
          result,
          organizationLevels
        ).id
      }
    });
  };

  closeForm = () => {
    const {
      hasChanged,
      result,
      criteria,
      attachmentList,
      stories
    } = this.state;

    if (hasChanged) {
      sessionStorage.setItem('result', JSON.stringify(result));
      sessionStorage.setItem('criteria', JSON.stringify(criteria));
      sessionStorage.setItem('attachmentList', JSON.stringify(attachmentList));
      sessionStorage.setItem('stories', JSON.stringify(stories));
      const message = 'Do you want to save the changes you made in this form?';
      const actions = {
        save: {
          name: 'Save',
          function: this.createNewWorkPackage
        },
        dontSave: {
          name: "Don't save",
          function: this.deleteStorageAndClose
        }
      };
      this.props.openModal(
        modalTypes.message,
        {
          message,
          actions,
          closeButtonTitle: 'Cancel'
        },
        'confirm'
      );
    } else {
      this.props.onClose();
    }
  };

  deleteStorageAndClose = () => {
    sessionStorage.removeItem('result');
    sessionStorage.removeItem('criteria');
    sessionStorage.removeItem('attachmentList');
    sessionStorage.removeItem('stories');
    this.props.onClose();
  };

  storeStories = () => {
    sessionStorage.setItem('stories', JSON.stringify(this.state.stories));
  };

  changeTab = (tabName) => {
    this.setState({ activeTab: tabName });
  };

  showModal = (message, actions) => {
    this.props.openModal(
      modalTypes.message,
      {
        message,
        actions,
        closeButtonTitle: 'Cancel'
      },
      'confirm'
    );
  };

  handleSelectChange = (currValue, name) => {
    const { result } = this.state;
    const { updateWorkPackage, onClose } = this.props;
    if (!result.id) {
      let newFeature;

      if (name === 'product_id') {
        newFeature = handleResultAuthorityChange(result, name, currValue);
      } else {
        newFeature = { ...result, [name]: currValue };
      }
      this.setState({ result: newFeature, hasChanged: true });
      return;
    }
    const field = { work_package: { [name]: currValue } };

    const message = `The ${
      name.split('_')[0]
    } you have selected is not part of the active space; this item will not be visible. Continue?`;

    const actions = {
      continue: {
        name: 'Continue',
        function: () => {
          updateWorkPackage(result.id, field);
          onClose();
        }
      }
    };

    if (name === 'product_id') {
      if (isProductAssociatedWithSpace(currValue))
        updateWorkPackage(result.id, field);
      else this.showModal(message, actions);
    } else if (name === 'team_id') {
      if (isTeamAssociatedWithSpace(currValue))
        updateWorkPackage(result.id, field);
      else this.showModal(message, actions);
    } else updateWorkPackage(result.id, field);
  };

  renderMetricsTab() {
    // disable metrics tab until r1 desecendents exist
    const r1Children = this.getMetricData() || 0;
    switch (r1Children) {
      // if there are points, disable = false
      case undefined:
        return true;
      case 0:
        return true;
      default:
        return false;
    }
  }

  render() {
    // if metrics data exists enable metrics
    const metricsDisabled = this.renderMetricsTab();

    if (!this.props.isOpen) {
      return null;
    }
    const {
      resultList,
      parents,
      capabilities,
      isLoading,
      products,
      teams,
      fromChild,
      metrics,
      selectedSpace,
      organizationLevels
    } = this.props;
    const {
      result,
      stories: newStories,
      criteria,
      attachmentList,
      activeTab,
      isShortNameValid
    } = this.state;

    const { product, team } = getModalProductTeam(
      result,
      selectedSpace,
      products,
      teams
    );

    const allowEdit = allows('update', result);
    const stories = result.id
      ? resultList.filter((r) => r.parent_id === result.id)
      : newStories;

    const storyOrganizationLevel = childOrganizationLevel(
      result,
      organizationLevels
    );

    const tabs = result.id
      ? ['details', 'children', 'metrics']
      : ['details', 'children'];

    return (
      <div
        id={`${result.id ? `workpackage-detail-${result.id}` : 'New Feature'}`}
        className="detail-modal workpackage-detail"
        tabIndex="-1"
        role="dialog"
      >
        <div className="detail-dialog" role="document">
          {isLoading ? <div id="loader" /> : null}
          <div className={`detail-content ${isLoading ? 'blur-loading' : ''}`}>
            <div className="modal-header detail-header">
              <DetailHeader
                title=""
                type="result"
                item={result}
                stories={stories}
                options={this.props.options}
                onClose={this.closeForm}
                handleUpdate={this.handleFieldChange}
                handleRemove={this.props.removeWorkPackageFromCadence}
                handleDelete={this.props.willDeleteWorkPackage}
                handleTypeAttributes={this.handleTypeAttributes}
                handleRestore={this.props.restoreWorkPackage}
                selectedSpace={selectedSpace}
                changeOrgLevel={this.changeOrgLevel}
                fromChild={fromChild}
              />
            </div>
            <div
              className={`modal-body ${activeTab} ${
                result.id ? '' : 'is-new'
              } result-body`}
            >
              <div className="short-name-box">
                <ShortNameField
                  autoFocus
                  shortName={result.short_name}
                  onNameChange={
                    result.id
                      ? this.handleFieldChange
                      : this.handleNewFieldChange
                  }
                  allowEdit={allowEdit}
                  onKeyUp={this.handleKeyPress}
                  isValid={isShortNameValid}
                />
                {result.id && <ShareButton />}
              </div>
              <ModalNavigation
                tabs={tabs}
                activeTab={activeTab}
                changeTab={this.changeTab}
                metricsDisabled={metricsDisabled}
              >
                <DetailsTab
                  feature={result}
                  products={this.props.products}
                  product={product}
                  teams={this.props.teams}
                  team={team}
                  user={this.props.user}
                  parents={parents}
                  capabilities={capabilities}
                  handleUpdate={
                    result.id
                      ? this.handleFieldChange
                      : this.handleNewFieldChange
                  }
                  onSelectChange={this.handleSelectChange}
                  addCriteria={this.addCriteria}
                  removeCriteria={this.removeCriteria}
                  criteria={criteria}
                  attachmentList={attachmentList}
                  addAttachment={this.addAttachment}
                  removeAttachment={this.removeAttachment}
                  onNewParentChange={this.handleNewParentChange}
                  onNewCapabilityChange={this.handleNewCapabilityChange}
                />
                <DetailStoriesTab
                  id={result.id}
                  result={result}
                  stories={stories}
                  disabled={!allowEdit}
                  isLoading={isLoading}
                  createTemporaryStory={this.createTemporaryStory}
                  updateTemporaryStory={this.updateTemporaryStory}
                  storeStories={this.storeStories}
                  storyOrganizationLevel={storyOrganizationLevel}
                />
                {result.id && (
                  <Metrics
                    metrics={metrics[result.id]}
                    workPackage={result}
                    stories={stories}
                    activeTab={activeTab}
                  />
                )}
              </ModalNavigation>
            </div>
            <div className="modal-footer">
              <div className="row footer-selects">
                {!result.id && (
                  <div className="col-md-3 save-cancel-container">
                    <div className="save-cancel-buttons">
                      <label>&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-md button-lavender"
                        onClick={this.createNewWorkPackage}
                        disabled={isLoading}
                      >
                        <strong>save</strong>
                      </button>
                      <button
                        type="button"
                        className="btn btn-md btn-default button-normal"
                        onClick={this.closeForm}
                        disabled={isLoading}
                      >
                        <strong>Cancel</strong>
                      </button>
                    </div>
                  </div>
                )}
                {/*
                <div className="col-md-3">
                  <label htmlFor="increment">PI</label>
                  <select
                    className="form-control"
                    value="PI 2 - COMMERCE"
                    disabled
                  >
                    <option value="PI 2 - COMMERCE">PI 2 - COMMERCE</option>
                  </select>
                </div>
                */}
                {this.state.activeTab === 'details' && (
                  <div className="col-md-3 targetSelect">
                    <TargetSelect
                      label="Target"
                      selected={result.target_id || -1}
                      disabled={!allowEdit}
                      onSelect={this.handleSelectChange}
                      workPackage={result}
                      fullWidth
                    />
                  </div>
                )}
              </div>
              <DetailStatus
                resultId={result.id}
                status={result.state || WorkPackageStatuses.NOT_READY}
                allowEdit={allowEdit}
                isNew={!result.id}
                handleNewFieldChange={this.handleNewFieldChange}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

FeatureDetail.propTypes = {
  id: PropTypes.number,
  result: PropTypes.object,
  options: PropTypes.object,
  isOpen: PropTypes.bool,
  resultList: PropTypes.array,
  parents: PropTypes.array,
  isLoading: PropTypes.bool,
  fromChild: PropTypes.bool,
  onClose: PropTypes.func,
  fetchUsers: PropTypes.func,
  getAllCadences: PropTypes.func,
  willDeleteWorkPackage: PropTypes.func,
  updateWorkPackage: PropTypes.func,
  removeWorkPackageFromCadence: PropTypes.func
};

const mapStateToProps = (state, ownProps) => {
  const { capabilities, epics } = state.workPackageReducer;
  const parents = capabilities.concat(epics);

  const { selectedSpace } = state.workPackageReducer;

  const organizationLevels = currentOrganization().organization_levels;

  // get data on children of this result
  const children = orderBy(
    state.workPackageReducer.all.filter(
      (child) => child.parent_id === ownProps.id
    ),
    ['level', 'id']
  );

  const result = ownProps.result
    ? ownProps.result
    : state.workPackageReducer.features.find((f) => f.id === ownProps.id) || {};

  return {
    children,
    parents,
    capabilities,
    selectedSpace,
    organizationLevels,
    resultList: state.workPackageReducer.stories,
    options: state.defaultOptionsReducer.defaultOptions,
    isLoading: state.workPackageReducer.isSending,
    products: state.productReducer.products,
    isUploading: state.attachmentReducer.isUploading,
    currentProducts: state.defaultOptionsReducer.currentProducts,
    currentTeams: state.defaultOptionsReducer.currentTeams,
    user: state.userReducer.user,
    teams: state.teamReducer.teams,
    metrics: state.workPackageReducer.metrics,
    result,
    planwallState: state.savedViewReducer.planwallStates.find(
      (pws) => pws.level === 2
    ),
    kanbanWallState: state.savedViewReducer.kanbanWallState,
    targets: state.targetReducer.targets,
    childrenProgress: state.workPackageReducer.metrics.childrenProgress
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateWorkPackage,
      willDeleteWorkPackage,
      getAllCadences,
      fetchUsers,
      createWorkPackage,
      openModal,
      updateModal,
      applicationError,
      getBurndownData,
      getLeanSeries,
      getWorkDensitySeries,
      getCumulativeFlowSeries,
      restoreWorkPackage,
      getChildrenProgress
    },
    dispatch
  );

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(FeatureDetail)
);
